import { useCallback, useEffect, useRef, useState } from 'react'
import {
  AlertTriangle, CheckCircle2, Download, FileUp, Loader2, Play, RefreshCw, ScanLine,
  Sheet, Upload,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { explain } from '@/shared/api/problem'
import { Modal } from '@/shared/ui/Modal'
import { EmptyState, ErrorStrip, LoadingBlock, Note } from '@/shared/ui/states'
import { fmtNumber } from '@/shared/lib/format'
import { sha256Hex } from '@/shared/lib/hash'
import { confirmUpload, putToPresignedUrl, reserveUpload } from '@/features/geography/api'
import {
  LOW_CONFIDENCE, applyImport, createImportBatch, dryRunImport, getOcrJob, listImportRows,
  parseOcr, parseProblems, parseRaw, reExtractImport, uploadFormBatch,
  type ImportBatch, type ImportRow, type OcrJob,
} from './importApi'

/**
 * Load a nomination list — from a spreadsheet, or from a scanned form.
 *
 * ## Two sources, one set of rules
 *
 * A scan takes one extra step: an engine reads the page, and only then does the ordinary
 * validation run. Everything after that is IDENTICAL — the server hands the scanned table to
 * the same validator the CSV goes through, so there is one set of rules and one set of
 * messages, and a reviewer who has used one path already knows the other.
 *
 * ## Waiting is a state this screen shows, not one it hides
 *
 * The first extraction after a container restart pays a model warm-up measured in tens of
 * seconds. The wizard polls the job and says what it is waiting for. A spinner with no
 * sentence would read as a hang, and the operator would upload the form again.
 *
 * ## Upload → check → apply, and the middle step writes nothing
 *
 * A general election is six seat types across 47 counties, 290 constituencies and 1,450
 * wards. Nobody keys that in, and nobody can unpick a half-applied import by hand — so the
 * server validates every row and reports every rejection before a single candidacy exists.
 * This screen exists to make that verdict readable: it opens on the REJECTED rows, because
 * those are the only ones anybody has to act on.
 *
 * ## Rejections show every reason at once
 *
 * The server collects all of a row's problems rather than the first. Showing them one at a
 * time would turn fixing a spreadsheet into one upload round-trip per mistake.
 *
 * ## Applying is deliberate and cannot be reached by accident
 *
 * The button only exists once a dry run has produced a verdict, it names exactly how many
 * rows it will write, and it says what it will NOT write.
 */
export function ImportWizard({
  open, electionId, onClose, onApplied,
}: {
  open: boolean
  electionId: string
  onClose: () => void
  onApplied: () => void | Promise<void>
}) {
  const [batch, setBatch] = useState<ImportBatch | null>(null)
  const [rows, setRows] = useState<ImportRow[] | null>(null)
  const [rowFilter, setRowFilter] = useState<'REJECTED' | 'VALID'>('REJECTED')
  const [busy, setBusy] = useState<null | 'upload' | 'dryRun' | 'apply' | 'reExtract'>(null)
  const [error, setError] = useState<string | null>(null)
  /** null until a source is chosen — the first thing the wizard asks. */
  const [source, setSource] = useState<null | 'FILE' | 'OCR_FORM'>(null)
  const [job, setJob] = useState<OcrJob | null>(null)
  const fileInput = useRef<HTMLInputElement>(null)

  // Reset between opens — a second import must not show the first one's verdict.
  useEffect(() => {
    if (open) {
      setBatch(null); setRows(null); setError(null); setRowFilter('REJECTED')
      setSource(null); setJob(null)
    }
  }, [open])

  /*
     Poll the EXTRACTION, not the batch.

     The batch does not move while the form is being read — `dryRun` answers 409 until the
     job finishes, deliberately, so the client's impatience never looks like a bad scan. The
     job is the thing that changes, so the job is the thing to watch.

     `batch?.ocrJobId` is in the dependency array, and it has to be: a re-extraction replaces
     the id, and a poll bound to the first one would watch a job nobody is waiting for.
  */
  useEffect(() => {
    const jobId = batch?.ocrJobId
    if (!jobId || batch?.statusCode !== 'UPLOADED') return
    let live = true
    let timer: ReturnType<typeof setTimeout>

    const poll = async () => {
      try {
        const next = await getOcrJob(jobId)
        if (!live) return
        setJob(next)
        if (next.statusCode === 'QUEUED' || next.statusCode === 'RUNNING') {
          timer = setTimeout(() => void poll(), 2000)
        }
      } catch (err) {
        if (live) setError(explain(err))
      }
    }
    void poll()
    return () => { live = false; clearTimeout(timer) }
  }, [batch?.ocrJobId, batch?.statusCode])

  const loadRows = useCallback(async (id: string, status: string) => {
    try {
      setRows(await listImportRows(id, { status, size: 300 }))
    } catch (err) {
      setError(explain(err))
    }
  }, [])

  // `batch` and `rowFilter` are both listed: switching the filter must refetch, and so must
  // a second dry run against the same batch.
  useEffect(() => {
    if (batch?.statusCode === 'VALIDATED' || batch?.statusCode === 'APPLIED') {
      void loadRows(batch.id, rowFilter)
    }
  }, [batch, rowFilter, loadRows])

  const upload = async (file: File) => {
    setBusy('upload')
    setError(null)
    try {
      const scan = source === 'OCR_FORM'
      const reserved = await reserveUpload({
        filename: file.name,
        /*
           The storage allowlist is EXACT and the browser's guess is not reliable. A CSV comes
           through as application/vnd.ms-excel on Windows; a scan saved by a phone can arrive
           as application/octet-stream. So the declared type is derived from the file's own
           extension for a scan, and hard-coded for a spreadsheet.

           It only has to satisfy the storage policy: the server sniffs the actual bytes
           before extracting, so a PDF mislabelled as a JPEG is still rasterised.
        */
        contentType: scan ? scanContentType(file) : 'text/csv',
        sizeBytes: file.size,
        purpose: scan ? 'DOCUMENT_SCAN' : 'VOTER_IMPORT',
      })
      await putToPresignedUrl(reserved.uploadUrl, file)
      await confirmUpload(reserved.fileId, await sha256Hex(file))
      const body = { electionId, fileId: reserved.fileId, notes: file.name }
      setBatch(scan ? await uploadFormBatch(body) : await createImportBatch(body))
    } catch (err) {
      setError(explain(err))
    } finally {
      setBusy(null)
    }
  }

  const reExtract = async () => {
    if (!batch) return
    setBusy('reExtract')
    setError(null)
    setJob(null)
    try {
      setBatch(await reExtractImport(batch.id))
    } catch (err) {
      setError(explain(err))
    } finally {
      setBusy(null)
    }
  }

  const dryRun = async () => {
    if (!batch) return
    setBusy('dryRun')
    setError(null)
    try {
      setBatch(await dryRunImport(batch.id))
    } catch (err) {
      setError(explain(err))
    } finally {
      setBusy(null)
    }
  }

  const apply = async () => {
    if (!batch) return
    setBusy('apply')
    setError(null)
    try {
      setBatch(await applyImport(batch.id))
      await onApplied()
    } catch (err) {
      setError(explain(err))
    } finally {
      setBusy(null)
    }
  }

  const scan = source === 'OCR_FORM' || batch?.sourceCode === 'OCR_FORM'
  const reading = scan && batch?.statusCode === 'UPLOADED'
    && (job === null || job.statusCode === 'QUEUED' || job.statusCode === 'RUNNING')
  const unreadable = scan && job?.statusCode === 'FAILED'
  const step = !batch ? 1 : batch.statusCode === 'VALIDATED' ? 3
    : batch.statusCode === 'APPLIED' ? 4 : 2

  return (
    <Modal open={open} onClose={onClose} width="lg"
      title="Import a nomination list"
      description="Upload, check what it would create, then apply it."
      footer={(
        <>
          <button type="button" className="erp-btn h-9 px-3 text-erp-sm" onClick={onClose}>
            {batch?.statusCode === 'APPLIED' ? 'Done' : 'Cancel'}
          </button>
          {/*
             Read the form again — for the failures a person can fix. Offered whenever the
             extraction did not produce usable rows, and never instead of the check: a form
             the machine read badly is still a form the machine read, and re-reading it is a
             choice rather than the default.
          */}
          {scan && batch && batch.statusCode !== 'APPLIED' && (unreadable
            || batch.statusCode === 'FAILED') && (
            <button type="button" disabled={busy !== null} onClick={() => void reExtract()}
              className="erp-btn h-9 px-3 text-erp-sm inline-flex items-center gap-2">
              {busy === 'reExtract' ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                : <RefreshCw className="h-3.5 w-3.5" />}
              Read it again
            </button>
          )}
          {batch && batch.statusCode !== 'APPLIED' && batch.statusCode !== 'FAILED' && (
            <button type="button" disabled={busy !== null || reading || unreadable}
              onClick={() => void dryRun()}
              title={reading ? 'The form is still being read' : undefined}
              className="erp-btn h-9 px-3 text-erp-sm inline-flex items-center gap-2">
              {busy === 'dryRun' ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                : <Play className="h-3.5 w-3.5" />}
              {batch.statusCode === 'VALIDATED' ? 'Check again'
                : scan ? 'Check the form' : 'Check the file'}
            </button>
          )}
          {batch?.statusCode === 'VALIDATED' && (
            <button type="button" disabled={busy !== null || batch.validRows === 0}
              onClick={() => void apply()}
              className="erp-btn-primary h-9 px-3 text-erp-sm inline-flex items-center gap-2">
              {busy === 'apply' && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
              Apply {fmtNumber(batch.validRows)} {batch.validRows === 1 ? 'row' : 'rows'}
            </button>
          )}
        </>
      )}>
      <div className="space-y-4">
        <StepRail step={step} />
        <ErrorStrip message={error} className="rounded-md border" />

        {/* ── 1a. which kind of thing are you holding? ──────────────────── */}
        {!batch && source === null && <SourceChoice onChoose={setSource} />}

        {/* ── 1b. upload ────────────────────────────────────────────────── */}
        {!batch && source !== null && (
          <div className="space-y-3">
            <button type="button" onClick={() => setSource(null)}
              className="text-erp-xs text-muted-foreground hover:text-foreground">
              ← Not that — choose a different source
            </button>
            <div
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => {
                e.preventDefault()
                const f = e.dataTransfer.files?.[0]
                if (f) void upload(f)
              }}
              className="rounded-md border border-dashed border-border px-4 py-8 text-center">
              {scan ? <ScanLine className="h-5 w-5 mx-auto mb-2 text-muted-foreground" />
                : <FileUp className="h-5 w-5 mx-auto mb-2 text-muted-foreground" />}
              <p className="text-erp-sm">
                {scan ? 'Drop a scan or a photo here, or' : 'Drop a CSV here, or'}
              </p>
              <button type="button" disabled={busy !== null}
                onClick={() => fileInput.current?.click()}
                className="erp-btn h-9 px-3 mt-2 inline-flex items-center gap-2 text-erp-sm">
                {busy === 'upload' ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  : <Upload className="h-3.5 w-3.5" />}
                Choose a file
              </button>
              <input ref={fileInput} type="file" hidden
                accept={scan ? SCAN_ACCEPT : '.csv,text/csv'}
                onChange={(e) => {
                  const f = e.target.files?.[0]
                  e.target.value = ''
                  if (f) void upload(f)
                }} />
            </div>
            {scan ? <ScanHint /> : <TemplateHint />}
          </div>
        )}

        {/* ── 2/3. verdict ──────────────────────────────────────────────── */}
        {batch && batch.statusCode === 'FAILED' && (
          <Note tone="bad">
            <strong>The file could not be used.</strong>
            <span className="block mt-1">{batch.error}</span>
          </Note>
        )}

        {batch && batch.statusCode === 'UPLOADED' && !scan && (
          <Note tone="info">
            Uploaded. Nothing has been read yet — press <strong>Check the file</strong> to
            validate every row without writing anything.
          </Note>
        )}

        {batch && batch.statusCode === 'UPLOADED' && scan && (
          <ExtractionState job={job} reading={reading} />
        )}

        {batch && (batch.statusCode === 'VALIDATED' || batch.statusCode === 'APPLIED') && (
          <>
            <Verdict batch={batch} />

            <div className="flex items-center gap-2">
              {(['REJECTED', 'VALID'] as const).map((f) => (
                <button key={f} type="button" onClick={() => setRowFilter(f)}
                  className={cn('h-8 px-2.5 rounded-md border text-erp-xs',
                    rowFilter === f
                      ? 'border-chocolate bg-chocolate-soft/40 text-chocolate font-medium'
                      : 'border-input text-muted-foreground hover:text-foreground')}>
                  {f === 'REJECTED'
                    ? `Rejected (${fmtNumber(batch.rejectedRows)})`
                    : `Ready (${fmtNumber(batch.validRows)})`}
                </button>
              ))}
            </div>

            {rows === null ? <LoadingBlock label="Loading rows…" />
              : rows.length === 0 ? (
                <EmptyState
                  title={rowFilter === 'REJECTED' ? 'Nothing was rejected' : 'No rows are ready'}
                  hint={rowFilter === 'REJECTED'
                    ? 'Every row in the file passed. Applying will create all of them.'
                    : 'Every row was rejected — fix the file and upload it again.'}
                />
              ) : <RowTable rows={rows} />}
          </>
        )}

        {batch?.statusCode === 'APPLIED' && (
          <Note tone="good">
            <strong>{fmtNumber(batch.appliedRows)} candidacies created</strong>, all at{' '}
            <strong>prospective</strong>. Importing a list does not clear anybody for a
            ballot — that is a separate, separately-authorised step.
          </Note>
        )}
      </div>
    </Modal>
  )
}

/**
 * What the OCR sidecar will take.
 *
 * PDFs are included because SCANNERS PRODUCE THEM BY DEFAULT — the server renders their
 * pages to images before extracting. Leaving PDF out of the picker would hide the most
 * common real upload behind a file dialog that refuses it.
 */
const SCAN_ACCEPT = 'image/jpeg,image/png,image/tiff,application/pdf,.jpg,.jpeg,.png,.tif,.tiff,.pdf'

/**
 * The content type to DECLARE for a scan.
 *
 * Derived from the extension rather than trusted from `file.type`: a phone-saved scan often
 * arrives as `application/octet-stream`, which the storage purpose policy refuses outright.
 * This only has to get the file into the bucket — the server sniffs the actual bytes before
 * extracting, so a PDF that came through mislabelled is still rasterised correctly.
 */
function scanContentType(file: File): string {
  const name = file.name.toLowerCase()
  if (name.endsWith('.pdf')) return 'application/pdf'
  if (name.endsWith('.png')) return 'image/png'
  if (name.endsWith('.tif') || name.endsWith('.tiff')) return 'image/tiff'
  if (name.endsWith('.jpg') || name.endsWith('.jpeg')) return 'image/jpeg'
  // Fall back to what the browser said, and let the storage policy be the judge.
  return file.type || 'image/jpeg'
}

/**
 * Spreadsheet or scan — asked first, because the two uploads behave differently.
 *
 * A CSV is readable the moment it lands. A scan is readable by nobody until a machine has
 * looked at it, which takes time and may not succeed. Offering one drop zone for both would
 * mean the wizard could not say which of those the user is about to be waiting for.
 */
function SourceChoice({ onChoose }: { onChoose: (s: 'FILE' | 'OCR_FORM') => void }) {
  const options = [
    {
      value: 'FILE' as const,
      icon: Sheet,
      title: 'A spreadsheet',
      body: 'A CSV exported from a nomination list. Read immediately, row by row.',
    },
    {
      value: 'OCR_FORM' as const,
      icon: ScanLine,
      title: 'A scanned form',
      body: 'A photo, scan or PDF of a nomination paper. A machine reads it first, then the '
        + 'same checks run — and you see what it read before anything is created.',
    },
  ]
  return (
    <div className="grid gap-2" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(230px, 1fr))' }}>
      {options.map((o) => (
        <button key={o.value} type="button" onClick={() => onChoose(o.value)}
          className="erp-card px-3 py-3 text-left hover:border-chocolate transition-colors">
          <o.icon className="h-4 w-4 mb-1.5 text-chocolate" />
          <div className="text-erp-sm font-medium">{o.title}</div>
          <p className="text-erp-xs text-muted-foreground mt-1">{o.body}</p>
        </button>
      ))}
    </div>
  )
}

/**
 * Where the extraction has got to.
 *
 * ## It says what it is waiting for, not just that it is waiting
 *
 * The first read after a container restart pays a model warm-up measured in tens of seconds.
 * A bare spinner reads as a hang, and an operator who thinks a screen has hung uploads the
 * form again — which queues a second extraction of the same page.
 *
 * ## A failure here is not the upload's failure
 *
 * The form is uploaded and stays uploaded. It can be read again, or keyed in by hand from
 * the ballot register. Saying both is the difference between a dead end and a next step.
 */
function ExtractionState({ job, reading }: { job: OcrJob | null; reading: boolean }) {
  if (reading) {
    return (
      <Note tone="info">
        <span className="inline-flex items-center gap-2">
          <Loader2 className="h-3.5 w-3.5 animate-spin shrink-0" />
          <span>
            <strong>Reading the form…</strong>
            <span className="block mt-1 text-erp-xs">
              {job?.statusCode === 'RUNNING'
                ? 'A machine is looking at the page now.'
                : 'Queued behind any other forms being read.'}{' '}
              The first read after a restart waits for the engine to warm up, so this can take
              up to a minute. Nothing is lost if you close this — the form keeps its place.
            </span>
          </span>
        </span>
      </Note>
    )
  }
  if (job?.statusCode === 'FAILED') {
    return (
      <Note tone="bad">
        <strong>The machine could not read this form.</strong>
        <span className="block mt-1 text-erp-xs">{job.error}</span>
        <span className="block mt-1.5 text-erp-xs">
          The upload is fine and the file is still here. Re-scan the page and press{' '}
          <strong>Read it again</strong>, or enter the candidates by hand from the ballot
          register — a form the machine cannot read is not a form nobody can read.
        </span>
      </Note>
    )
  }
  if (job?.statusCode === 'SUCCEEDED') {
    return (
      <Note tone="good">
        <strong>Read.</strong>{' '}
        {job.pageCount ?? 1} {job.pageCount === 1 ? 'page' : 'pages'}
        {job.engine && <> · {job.engine}</>}
        {job.meanConfidence != null && <> · average confidence {pct(job.meanConfidence)}</>}
        <span className="block mt-1 text-erp-xs">
          Nothing has been created yet. Press <strong>Check the form</strong> to run the same
          validation a spreadsheet goes through, and see every row before it is applied.
        </span>
      </Note>
    )
  }
  return <LoadingBlock label="Checking the extraction…" />
}

const pct = (v: number) => `${Math.round(v * 100)}%`

function Verdict({ batch }: { batch: ImportBatch }) {
  return (
    <div className="grid gap-2" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))' }}>
      <Stat label="Rows read" value={batch.totalRows} />
      <Stat label="Ready" value={batch.validRows} tone="good" />
      <Stat label="Rejected" value={batch.rejectedRows} tone={batch.rejectedRows ? 'bad' : undefined} />
      {batch.statusCode === 'APPLIED' && (
        <Stat label="Created" value={batch.appliedRows} tone="good" />
      )}
    </div>
  )
}

function Stat({ label, value, tone }: { label: string; value: number; tone?: 'good' | 'bad' }) {
  return (
    <div className="erp-card px-3 py-2">
      <div className="text-erp-xs uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className={cn('text-erp-lg font-semibold tabular-nums',
        tone === 'good' && 'text-emerald-700 dark:text-emerald-400',
        tone === 'bad' && 'text-red-700 dark:text-red-400')}>
        {fmtNumber(value)}
      </div>
    </div>
  )
}

/**
 * The rows, showing the line as UPLOADED beside its problems.
 *
 * The raw row is what the reviewer has to correct in their spreadsheet, so it is what is
 * displayed — our parse of it would be a different string and would send them looking for
 * text that is not in their file.
 *
 * ## For a scan, "as uploaded" means "as the machine read it"
 *
 * Which is exactly what a reviewer needs to compare against the paper. Each cell carries the
 * engine's confidence, and the weak ones are marked — **marked, not removed**. Confidence is
 * the machine's opinion of its own reading, not a probability that the value is right, and a
 * screen that hid low-confidence cells would be making that opinion into a decision. The
 * human decides; this shows them where to look first.
 */
function RowTable({ rows }: { rows: ImportRow[] }) {
  const scanned = rows.some((r) => parseOcr(r.raw) !== null)
  return (
    <div className="axis-table-scroll max-h-80 overflow-y-auto border border-border rounded-md">
      <table className="w-full border-collapse">
        <thead className="sticky top-0 bg-card">
          <tr>
            <th className="erp-grid-header text-right w-14">{scanned ? 'Row' : 'Line'}</th>
            {scanned && <th className="erp-grid-header text-right w-12">Page</th>}
            <th className="erp-grid-header text-left">
              {scanned ? 'What the machine read' : 'As uploaded'}
            </th>
            <th className="erp-grid-header text-left">What is wrong</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const raw = parseRaw(r.raw)
            const problems = parseProblems(r.problems)
            const ocr = parseOcr(r.raw)
            return (
              <tr key={r.id}>
                <td className="erp-grid-cell text-right tabular-nums align-top">{r.rowNumber}</td>
                {scanned && (
                  <td className="erp-grid-cell text-right tabular-nums align-top text-muted-foreground">
                    {ocr?.page ?? '—'}
                  </td>
                )}
                <td className="erp-grid-cell align-top">
                  <div className="text-erp-xs font-mono break-all flex flex-wrap gap-x-3 gap-y-0.5">
                    {Object.entries(raw)
                      .filter(([, v]) => v != null && v !== '')
                      .map(([k, v]) => {
                        const confidence = ocr?.confidence?.[k]
                        const weak = confidence != null && confidence < LOW_CONFIDENCE
                        return (
                          <span key={k}
                            title={confidence != null
                              ? `the engine was ${pct(confidence)} sure of this`
                              : undefined}
                            className={cn(weak && 'rounded-sm bg-amber-100 px-1 '
                              + 'text-amber-900 dark:bg-amber-500/20 dark:text-amber-200')}>
                            {k}={v}
                            {weak && (
                              <span className="ml-1 not-italic opacity-70">
                                {pct(confidence)}
                              </span>
                            )}
                          </span>
                        )
                      })}
                  </div>
                </td>
                <td className="erp-grid-cell align-top">
                  {problems.length === 0 ? (
                    <span className="inline-flex items-center gap-1 text-erp-xs text-emerald-700 dark:text-emerald-400">
                      <CheckCircle2 className="h-3 w-3" /> Ready
                    </span>
                  ) : (
                    <ul className="space-y-0.5">
                      {problems.map((p, i) => (
                        <li key={i} className="text-erp-xs text-red-700 dark:text-red-400 flex gap-1.5">
                          <AlertTriangle className="h-3 w-3 mt-0.5 shrink-0" />
                          <span>
                            {p.field && <strong className="font-mono">{p.field}: </strong>}
                            {p.message}
                          </span>
                        </li>
                      ))}
                    </ul>
                  )}
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

/**
 * What a scan has to show, said BEFORE the upload.
 *
 * The reader finds the table by looking for a row of column headings, so a page whose
 * headings are cropped off or illegible produces "no table found" — and that is far cheaper
 * to prevent here than to diagnose afterwards.
 */
function ScanHint() {
  return (
    <div className="erp-card px-3 py-2.5 text-erp-xs space-y-2">
      <div className="font-medium">What the scan needs</div>
      <ul className="space-y-1 text-muted-foreground">
        <li>
          <strong className="text-foreground">The column headings must be in the picture.</strong>{' '}
          The reader finds the table by them — at least two of{' '}
          <code className="font-mono text-foreground">seat</code>,{' '}
          <code className="font-mono text-foreground">unit_code</code>,{' '}
          <code className="font-mono text-foreground">candidate_name</code>,{' '}
          <code className="font-mono text-foreground">party_code</code>. A crest or a title
          above them is fine and expected.
        </li>
        <li>The page the right way up, and in focus. Whole page in frame, not a corner.</li>
        <li>
          <strong className="text-foreground">PDFs are fine</strong> — scanners produce them by
          default. Pages are rendered before they are read, up to 30 of them.
        </li>
        <li>
          Multiple sheets in one PDF are fine too: only the first needs its headings, and the
          rest are read as continuations.
        </li>
      </ul>
      <p className="text-muted-foreground/80">
        Nothing is created from the scan directly. It is read, then checked against exactly
        the same rules a spreadsheet goes through, and you see every row first.
      </p>
    </div>
  )
}

/**
 * What the file must contain.
 *
 * Shown BEFORE the upload rather than as an error afterwards: a missing column fails the
 * whole file, and finding that out after a round trip is a wasted one.
 */
function TemplateHint() {
  const csv = 'seat,unit_code,candidate_name,party_code,independent,ballot_name,ballot_number,nominated_on\n'
    + 'GOVERNOR,047,Achieng Otieno,UA,,A. Otieno,1,2027-05-20\n'
    + 'MP,047001,Barasa Wekesa,,yes,,2,2027-05-20\n'

  return (
    <div className="erp-card px-3 py-2.5 text-erp-xs space-y-2">
      <div className="font-medium">What the file needs</div>
      <ul className="space-y-1 text-muted-foreground">
        <li><code className="font-mono text-foreground">seat</code> — PRESIDENT, GOVERNOR, SENATOR, WOMAN_REP, MP or MCA. Required.</li>
        <li><code className="font-mono text-foreground">unit_code</code> — the code of the place, AT the seat's level: a county for a governor, a ward for an MCA. Required.</li>
        <li><code className="font-mono text-foreground">candidate_name</code> — required. Somebody not yet in the register is added.</li>
        <li><code className="font-mono text-foreground">party_code</code> — required unless the row says <code className="font-mono text-foreground">independent</code>.</li>
        <li><code className="font-mono text-foreground">ballot_name</code>, <code className="font-mono text-foreground">ballot_number</code>, <code className="font-mono text-foreground">nominated_on</code> — optional.</li>
      </ul>
      <p className="text-muted-foreground/80">
        Column names are matched ignoring case and spacing, so "Unit Code" and{' '}
        <code className="font-mono">unit_code</code> are the same column.
      </p>
      <a download="nomination-list-template.csv"
        href={`data:text/csv;charset=utf-8,${encodeURIComponent(csv)}`}
        className="inline-flex items-center gap-1.5 text-chocolate hover:underline">
        <Download className="h-3 w-3" /> Download a template
      </a>
    </div>
  )
}

const STEPS = ['Upload', 'Check', 'Review', 'Applied']

function StepRail({ step }: { step: number }) {
  return (
    <ol className="flex items-center gap-1 text-erp-xs">
      {STEPS.map((label, i) => {
        const n = i + 1
        return (
          <li key={label} className={cn(
            'h-7 px-2.5 inline-flex items-center gap-1.5 rounded-md border',
            n === step ? 'border-chocolate bg-chocolate-soft/40 text-chocolate font-medium'
              : n < step ? 'border-border text-muted-foreground'
                : 'border-dashed border-border text-muted-foreground/50',
          )}>
            <span className="tabular-nums">{n}</span>{label}
          </li>
        )
      })}
    </ol>
  )
}
